import React from "react";
import { Divider, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { IStats } from "../../shared/types";
import { formatSignedPercent, formatSignedUsd } from "../../utils/formatters";
import { MARKET_RESIDUAL_HELP, bonusYieldUsd, hasReturnBasis, pnlBreakdownDisplayed } from "../../utils/botMetrics";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import DetailItem from "../../components/cards/subcomponents/DetailItem";
import { SurfaceCard } from "../../components/ui/SurfaceCard";
import { Metric } from "../../components/ui/Metric";

interface LPPerformanceCardProps {
  stats: IStats;
  totalVolume: number;
  isAdmin: boolean;
}

/**
 * Total P&L (same figure and name as the Capital flow ledger) as a breakdown that adds up:
 * market (residual) + pool fees + bonus yield (+ lending) = total.
 */
const LPPerformanceCard: React.FC<LPPerformanceCardProps> = ({ stats }) => {
  const theme = useTheme();
  const { fees, bonus, lending, market } = pnlBreakdownDisplayed(
    stats.pnlUSD || 0,
    stats.totalPoolFeesUSDOrig || 0,
    bonusYieldUsd(stats),
    stats.lendingPnl || 0
  );
  const signColor = (v: number) =>
    v > 0 ? theme.palette.success.main : v < 0 ? theme.palette.error.main : undefined;

  return (
    <SurfaceCard tone={stats.pnlUSD >= 0 ? "positive" : "negative"} sx={{ height: "100%" }}>
      <Metric
        icon={TrendingUpRoundedIcon}
        label="Total P&L"
        value={hasReturnBasis(stats) ? formatSignedPercent(stats.pnlPerCapital) : "—"}
        sub={formatSignedUsd(stats.pnlUSD)}
        tone="auto"
        signal={stats.pnlUSD}
        size="xl"
        inlineSub
      />
      <Divider sx={{ my: 2 }} />
      <Stack spacing={0.25}>
        <DetailItem
          label="Market & IL"
          value={formatSignedUsd(market)}
          valueColor={signColor(market)}
          sections={[{ title: "Market & IL", items: [{ label: "", value: MARKET_RESIDUAL_HELP }] }]}
        />
        <DetailItem label="Pool fees" value={formatSignedUsd(fees)} valueColor={signColor(fees)} />
        <DetailItem label="Bonus yield" value={formatSignedUsd(bonus)} valueColor={signColor(bonus)} />
        {lending !== 0 && (
          <DetailItem label="Lending" value={formatSignedUsd(lending)} valueColor={signColor(lending)} />
        )}
      </Stack>
    </SurfaceCard>
  );
};

export default LPPerformanceCard;
